import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthImage } from '@/shared/hooks';
import { downloadAuthFile } from '@/shared/utils/download';
import { useDeleteGeneration } from '../hooks';
import type { HistoryItem } from '../types';

interface HistoryDetailProps {
  readonly item: HistoryItem;
  readonly onClose: () => void;
  readonly onReuse: (item: HistoryItem) => void;
}

export function HistoryDetail({ item, onClose, onReuse }: HistoryDetailProps) {
  const { t } = useTranslation();
  const deleteMutation = useDeleteGeneration();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const hasImage = item.status === 'completed' && item.result_path;
  const imagePath = hasImage ? `/api/history/${item.id}/image` : null;
  const blobUrl = useAuthImage(imagePath);

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate(item.id, {
      onSuccess: onClose,
    });
    setShowDeleteConfirm(false);
  };

  const handleDownload = () => {
    downloadAuthFile(`/api/history/${item.id}/image`, `generation-${item.id}.png`);
  };

  const statusColor =
    item.status === 'completed'
      ? 'var(--tg-theme-button-color, #3b82f6)'
      : item.status === 'failed'
        ? 'var(--tg-theme-destructive-text-color, #ef4444)'
        : 'var(--tg-theme-hint-color, #999)';

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-tg-link transition-opacity hover:opacity-80 active:opacity-60"
        >
          {t('common.back')}
        </button>
        <span
          className="text-sm font-medium"
          style={{ color: statusColor }}
        >
          {t(`history.status.${item.status}`)}
        </span>
      </div>

      {/* Image */}
      {blobUrl && (
        <div
          className="overflow-hidden rounded-xl border"
          style={{
            borderColor: 'color-mix(in srgb, var(--tg-theme-hint-color) 10%, transparent)',
            boxShadow: '0 4px 16px color-mix(in srgb, var(--tg-theme-hint-color) 8%, transparent)',
          }}
        >
          <img
            src={blobUrl}
            alt={item.positive_prompt}
            className="w-full animate-fade-in"
          />
        </div>
      )}

      {/* Error */}
      {item.error_message && (
        <div
          className="rounded-lg p-3 text-sm bg-tg-secondary text-tg-destructive"
          style={{
            border: '1px solid color-mix(in srgb, var(--tg-theme-destructive-text-color, #ef4444) 20%, transparent)',
          }}
        >
          {item.error_message}
        </div>
      )}

      {/* Parameters */}
      <div
        className="flex flex-col gap-0 rounded-xl border bg-tg-secondary overflow-hidden"
        style={{
          borderColor: 'color-mix(in srgb, var(--tg-theme-hint-color) 10%, transparent)',
        }}
      >
        <ParamRow label={t('history.detail.checkpoint')} value={item.checkpoint} />
        <ParamRow label={t('history.detail.prompt')} value={item.positive_prompt} />
        {item.negative_prompt && (
          <ParamRow label={t('history.detail.negativePrompt')} value={item.negative_prompt} />
        )}
        <ParamRow label={t('history.detail.dimensions')} value={`${item.width}x${item.height}`} />
        <ParamRow label={t('history.detail.sampler')} value={`${item.sampler_name} / ${item.scheduler}`} />
        <ParamRow label={t('history.detail.steps')} value={String(item.steps)} />
        <ParamRow label={t('history.detail.cfgScale')} value={String(item.cfg_scale)} />
        <ParamRow label={t('history.detail.seed')} value={String(item.seed)} />
        {item.loras.length > 0 && (
          <ParamRow
            label={t('history.detail.loras')}
            value={item.loras.map((l) => `${l.resource_id} (${l.weight})`).join(', ')}
          />
        )}
        <ParamRow
          label={t('history.detail.created')}
          value={new Date(item.created_at).toLocaleString()}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onReuse(item)}
          className="flex-1 rounded-xl py-2.5 text-sm font-semibold text-tg-button-text transition-all duration-200 active:scale-[0.98]"
          style={{
            background: 'linear-gradient(135deg, var(--tg-theme-button-color), color-mix(in srgb, var(--tg-theme-button-color), black 15%))',
            boxShadow: '0 2px 8px color-mix(in srgb, var(--tg-theme-button-color) 30%, transparent)',
          }}
        >
          {t('history.reuse')}
        </button>
        {blobUrl && (
          <button
            type="button"
            onClick={handleDownload}
            className="rounded-xl bg-tg-secondary px-4 py-2.5 text-sm font-medium text-tg-text transition-all duration-200 active:scale-[0.98]"
            style={{
              border: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 20%, transparent)',
            }}
          >
            {t('generate.downloadImage')}
          </button>
        )}
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
          className="rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200 active:scale-[0.98] disabled:opacity-50 text-tg-destructive"
          style={{
            border: '1px solid color-mix(in srgb, var(--tg-theme-destructive-text-color, #ef4444) 30%, transparent)',
          }}
        >
          {deleteMutation.isPending ? '...' : t('common.delete')}
        </button>
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fade-in backdrop-blur-sm">
          <div className="mx-4 flex max-w-sm flex-col gap-3 rounded-xl border border-tg-hint/10 bg-tg-bg p-4 animate-scale-in">
            <p className="text-center text-sm text-tg-text">
              {t('history.confirmDelete')}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 rounded-xl bg-tg-secondary py-2 text-sm font-medium text-tg-text transition-all duration-200 active:scale-[0.98]"
                style={{
                  border: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 20%, transparent)',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 rounded-xl py-2 text-sm font-semibold text-tg-button-text transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                style={{
                  background: 'var(--tg-theme-destructive-text-color, #ef4444)',
                }}
              >
                {deleteMutation.isPending ? '...' : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ParamRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div
      className="flex justify-between gap-2 px-3 py-2 last:border-b-0"
      style={{ borderBottom: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 10%, transparent)' }}
    >
      <span className="shrink-0 text-xs text-tg-hint">
        {label}
      </span>
      <span className="max-w-[60%] break-words text-right text-xs text-tg-text">
        {value}
      </span>
    </div>
  )
}
